import classNames from 'classnames';
import { Formik } from 'formik';
import React, { FC } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import {
  PolicyDetailsType,
  VehicleOptionsResponse,
  VehicleType,
} from '@clearcover/core-app-types';

import {
  Asset,
  Checkbox,
  ModalLoading,
  VehicleOutlineIcon,
} from '@clearcover/atlas';
import {
  FormInput,
  FormModalContent,
  FormModalFooter,
  FormModalHeader,
  FormModalUnderWritingErrors,
} from 'Shared/components';
import { STATE_OPTIONS } from 'Shared/constants/states';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';
import { useApi, useLazyApi } from 'Shared/services/Api';
import { combineStrings } from 'Shared/utils';

import { usePolicyContext } from 'Policies/utils/PolicyContext/PolicyContext';

import { yupZipShortValidator } from 'Admin/utils/formValidators';
import { draftEndpointModifier } from 'Policies/utils';
import styles from './styles.module.css';

const ADDRESS_VALIDATION = Yup.object({
  address1: Yup.string().required('Street address is required'),
  address2: Yup.string(),
  city: Yup.string().required('City is required'),
  state: Yup.string()
    .oneOf(Object.keys(STATE_OPTIONS))
    .required('State is required'),
  zip: yupZipShortValidator().required('ZIP Code is required'),
});

const FORM_VALIDATION = Yup.object({
  garagingAddressSameAsMailing: Yup.boolean(),
  addresses: Yup.object().when('garagingAddressSameAsMailing', {
    is: true,
    then: Yup.object({
      mailing: ADDRESS_VALIDATION,
    }),
    otherwise: Yup.object({
      mailing: ADDRESS_VALIDATION,
      garaging: ADDRESS_VALIDATION,
    }),
  }),
  updateGaragingAddresses: Yup.boolean(),
});

export const EditDetailsForm: FC<{
  policyDetails: PolicyDetailsType;
  vehicles: Array<VehicleType>;
  close: () => void;
}> = ({ policyDetails, vehicles, close }) => {
  const history = useHistory();
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();
  const {
    refetchHandler,
    details: { draftType },
    flags: { renewalQuotesEnabled },
  } = usePolicyContext();

  const [{ data: vehicleOptionsData, loading: isLoading }] =
    useApi<VehicleOptionsResponse>(
      `/options/vehicle?identifier=${policyDetails.filingIdentifier}&region=${policyDetails.policyRegion}`
    );

  const [, editDetails] = useLazyApi<{
    draftId: string;
  }>({
    method: 'POST',
    url: `/policies/${policyId}/editPolicyDetails${draftEndpointModifier(
      draftType,
      renewalQuotesEnabled
    )}`,
  });

  const { underwritingErrors, setUnderwritingErrors, hasUnderwritingErrors } =
    useUnderwritingErrors();

  if (isLoading) {
    return <ModalLoading data-testid="policy-details-loading" />;
  }

  const showVehicleGaragingAddress = vehicleOptionsData.showGaragingAddress;

  return (
    <Formik
      initialValues={{
        addresses: policyDetails.addresses,
        updateGaragingAddresses: true,
        garagingAddressSameAsMailing:
          policyDetails.addresses.garaging.address1 ===
            policyDetails.addresses.mailing.address1 &&
          policyDetails.addresses.garaging.address2 ===
            policyDetails.addresses.mailing.address2 &&
          policyDetails.addresses.garaging.city ===
            policyDetails.addresses.mailing.city &&
          policyDetails.addresses.garaging.state ===
            policyDetails.addresses.mailing.state &&
          policyDetails.addresses.garaging.zip ===
            policyDetails.addresses.mailing.zip,
      }}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        editDetails({
          data: {
            address: values.garagingAddressSameAsMailing
              ? values.addresses.mailing
              : values.addresses.garaging,
            mailingAddress: values.addresses.mailing,
            updateGaragingAddresses: values.updateGaragingAddresses,
          },
        })
          .then(({ data }) => {
            draftId === data.draftId
              ? refetchHandler()
              : history.push(`/policies/${policyId}/draft/${data.draftId}`);
            history.push(`/policies/${policyId}/draft/${data.draftId}`);
          })
          .catch((status) => {
            setUnderwritingErrors(status);
            setSubmitting(false);
            setStatus(hasUnderwritingErrors(status) ? '' : status.message);
          });
      }}
      validationSchema={FORM_VALIDATION}
      validateOnMount
    >
      {({ handleBlur, values, touched, errors, setFieldValue }) => {
        return (
          <>
            <FormModalHeader>Edit Policy Details</FormModalHeader>
            <FormModalContent>
              <div
                className={classNames('fs-exclude', styles.section)}
                data-testid="mailing-address-form"
              >
                <h1>Mailing Address</h1>
                <FormInput
                  variant="addressAutocomplete"
                  prefix="mailingAddress"
                  allowStateChange={false}
                  values={{
                    address1: values.addresses.mailing.address1,
                    address2: values.addresses.mailing.address2,
                    city: values.addresses.mailing.city,
                    state: values.addresses.mailing.state,
                    zip: values.addresses.mailing.zip,
                  }}
                  onAddressSelect={(selectedOption) => {
                    const { city, streetLine } = selectedOption;
                    setFieldValue('addresses.mailing.address1', streetLine);
                    setFieldValue('addresses.mailing.city', city);
                    setFieldValue('addresses.mailing.zip', '');
                  }}
                  onAddressLine1Change={(value) =>
                    setFieldValue('addresses.mailing.address1', value)
                  }
                  onAddressLine2Change={(value) =>
                    setFieldValue('addresses.mailing.address2', value)
                  }
                  onCityChange={(value) =>
                    setFieldValue('addresses.mailing.city', value)
                  }
                  onStateChange={(value) =>
                    setFieldValue('addresses.mailing.state', value)
                  }
                  onZipChange={(value) =>
                    setFieldValue('addresses.mailing.zip', value)
                  }
                  errorMessages={{
                    address1: touched.addresses?.mailing?.address1
                      ? errors.addresses?.mailing?.address1
                      : undefined,
                    address2: touched.addresses?.mailing?.address2
                      ? errors.addresses?.mailing?.address2
                      : undefined,
                    city: touched.addresses?.mailing?.city
                      ? errors.addresses?.mailing?.city
                      : undefined,
                    state: touched.addresses?.mailing?.state
                      ? errors.addresses?.mailing?.state
                      : undefined,
                    zip: touched.addresses?.mailing?.zip
                      ? errors.addresses?.mailing?.zip
                      : undefined,
                  }}
                  onBlur={handleBlur}
                />
              </div>

              <Checkbox
                name="garaging-address-same-as-mailing"
                value={values.garagingAddressSameAsMailing}
                label="My garaging address is the same as my mailing address"
                onChange={(isChecked) =>
                  setFieldValue('garagingAddressSameAsMailing', isChecked)
                }
              />

              <div
                className={classNames('fs-exclude', styles.section)}
                data-testid="garaging-address-form"
                hidden={values.garagingAddressSameAsMailing}
              >
                <h1>Garaging Address</h1>
                <FormInput
                  variant="addressAutocomplete"
                  prefix="garagingAddress"
                  allowStateChange={false}
                  values={{
                    address1: values.addresses.garaging.address1,
                    address2: values.addresses.garaging.address2,
                    city: values.addresses.garaging.city,
                    state: values.addresses.garaging.state,
                    zip: values.addresses.garaging.zip,
                  }}
                  onAddressSelect={(selectedOption) => {
                    const { city, streetLine } = selectedOption;
                    setFieldValue('addresses.garaging.address1', streetLine);
                    setFieldValue('addresses.garaging.city', city);
                    setFieldValue('addresses.garaging.zip', '');
                  }}
                  onAddressLine1Change={(value) =>
                    setFieldValue('addresses.garaging.address1', value)
                  }
                  onAddressLine2Change={(value) =>
                    setFieldValue('addresses.garaging.address2', value)
                  }
                  onCityChange={(value) =>
                    setFieldValue('addresses.garaging.city', value)
                  }
                  onStateChange={(value) =>
                    setFieldValue('addresses.garaging.state', value)
                  }
                  onZipChange={(value) =>
                    setFieldValue('addresses.garaging.zip', value)
                  }
                  errorMessages={{
                    address1: touched.addresses?.garaging?.address1
                      ? errors.addresses?.garaging?.address1
                      : undefined,
                    address2: touched.addresses?.garaging?.address2
                      ? errors.addresses?.garaging?.address2
                      : undefined,
                    city: touched.addresses?.garaging?.city
                      ? errors.addresses?.garaging?.city
                      : undefined,
                    state: touched.addresses?.garaging?.state
                      ? errors.addresses?.garaging?.state
                      : undefined,
                    zip: touched.addresses?.garaging?.zip
                      ? errors.addresses?.garaging?.zip
                      : undefined,
                  }}
                  onBlur={handleBlur}
                />
              </div>

              {showVehicleGaragingAddress ? (
                <div
                  className={styles.vehicleGaraging}
                  data-testid="vehicle-garaging-address-form"
                >
                  <div className={styles.vehicleGaragingHeader}>
                    <h2>
                      <Asset
                        icon={VehicleOutlineIcon}
                        description="Vehicle Garaging"
                        height={24}
                        width={24}
                      />
                      Vehicle Garaging
                    </h2>
                  </div>
                  <div className={styles.vehicleGaragingBody}>
                    <Checkbox
                      name="vehicle-garaging"
                      value={values.updateGaragingAddresses}
                      label={
                        values.garagingAddressSameAsMailing
                          ? 'Apply my mailing address to all vehicles'
                          : 'Apply my garaging address to all vehicles'
                      }
                      onChange={(isChecked) =>
                        setFieldValue('updateGaragingAddresses', isChecked)
                      }
                    />
                    <ul>
                      {vehicles.map((vehicle, index) => (
                        <li key={`${vehicle.vin}-${index}`}>
                          {combineStrings([
                            vehicle.year,
                            vehicle.make,
                            vehicle.model,
                          ])}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </FormModalContent>
            <FormModalUnderWritingErrors errors={underwritingErrors} />
            <FormModalFooter identifier="edit-details" close={close} />
          </>
        );
      }}
    </Formik>
  );
};

EditDetailsForm.displayName = 'EditDetailsForm';
